import { PaymentStatusTable } from '@aidkitorg/component-library';
import { CellRendererProps } from '@aidkitorg/component-library/dist/types/src/PaymentStatusTable';
import * as v0 from '@aidkitorg/types/lib/survey';
import { useMemo } from 'react';
import { useLocalizedStrings } from '../Localization';
import { snakeToEnglish } from '../Util';
import ExploreLink from './ExploreLink';

export default function PaymentDashboard({ component, results }: { component: v0.PaymentDashboard } & { results?: any[] }) {

  const L = useLocalizedStrings();
  const dimensions = component.dimensions ?? ['payment'];

  if (!results?.length) {
    return <div>No results to show!</div>
  }

  const rows = useMemo(() => results.map(r => ({
    dimensions: Object.assign(
      Object.fromEntries(dimensions.map(d => [d, r.info[d]])),
      { payment: r.payment }
    ),
    statuses: r.statuses
  })), [results]);


  function drillDownCell(props: CellRendererProps) {
    if(props.type === 'name') {
      return component.friendlyDimensionNames ? snakeToEnglish(props.name) : props.name;
    }

    const { dimensions, status, value } = props;

    const base: string[] = ['legal_name', dimensions.payment].concat(Object.keys(dimensions).filter(d => d !== 'payment'))

    if (status === 'failed' || status === 'cancelled') {
      base.push(...['unresolved_failure', 'failure', 'failure_type', 'failure_date'].map(suffix => [dimensions.payment, suffix].join('_')));
    }

    const paymentFields = ["status", "type", "amount", "date created", "transaction ref"]
      .map(paymentField => ({
        kind: 'Field',
        field: dimensions.payment,
        lookup: {
          kind: 'Payment',
          prop: paymentField
        }
      }) as v0.Field);

    return <div className="group">
      <span className="group-hover:hidden">{value}</span>
      <div className="hidden group-hover:block">
        <ExploreLink query={{
          query: {
            kind: 'Applicant Table',
            columns: base.map(field => ({ kind: 'Field', field }) as v0.Field).concat(paymentFields),
            filter: {
              kind: 'Click',
              expr: {
                kind: 'And',
                clauses: [
                  {
                    kind: 'Exists',
                    field: dimensions.payment
                  },
                  // "values" represents an unbucketed set of rows, 
                  // so we don't want to accidentally use that in the condition 
                  // as it will always result in 0 rows found.
                  ...Object.entries(dimensions).filter(([k, v]) => k !== 'payment' && v !== 'values').map(([name, dim]) => ({
                    kind: 'Equals',
                    field: name,
                    value: dim
                  }) as v0.FieldEquals),
                  {
                    kind: 'Value Equals',
                    equals: {
                      kind: 'StringValue',
                      value: status.toUpperCase()
                    },
                    value: {
                      kind: 'Field',
                      field: dimensions.payment,
                      lookup: {
                        kind: 'Payment',
                        prop: 'status'
                      }
                    }
                  }
                ]
              }
            }
          }
        }} />
      </div>
    </div>;
  }

  return <>
    <PaymentStatusTable
      cellRenderer={drillDownCell}
      translations={{
        total: 'Total',
        payment: 'Payment Names',
      }}
      data={rows}
      sort={['desc', 'asc']}
      dimensions={dimensions as [string, string]}
    />
  </>;
}
